import { Activity, AlertTriangle, HelpCircle, Info, Loader2, Package, RefreshCw, Repeat, ShieldCheck, Zap } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { fetchProofOfWorkSelections } from '@/api/admin';
import ProofOfWorkLivePanel from './ProofOfWorkLivePanel';

/**
 * Proof of Work: Random Selection — read-only admin subpage.
 *
 * Purpose
 * -------
 *   Sanity-check page that lets an operator visually verify the v9 random
 *   engine is genuinely picking positions uniformly across the pool.
 *
 *   Two tables show the last 50 selected fillers (Phase 1) and last 50
 *   selected roots (Phase 2). Each row carries the Position ID, owning
 *   username, a colored Type Badge (Single / Subpack / Rebuy / Auto Rebuy
 *   / Unknown), the Purchase / Creation Timestamp, and the Selection
 *   Timestamp the heartbeat stamped at pop time.
 *
 *   If the Purchase Timestamps in either table are mixed (different ages
 *   appearing in adjacent rows), uniform random distribution is alive.
 *   If they sort strictly ascending, FIFO has regressed.
 *
 * Backward-compat notes (post-v10 deploy):
 *   - Positions queued BEFORE v10 deploy show source="unknown" and an
 *     empty created_at. The UI renders these with a grey "Unknown" badge
 *     and a "—" Purchase Timestamp. They cycle out over a few hours of
 *     production activity and the page becomes fully tagged.
 *   - The selected_at column is ALWAYS real \u2014 stamped by the heartbeat
 *     at pop time \u2014 so randomness is still provable from the very first
 *     tick after deploy via the Selection Timestamp column.
 *
 * Polling guardrail (CRITICAL)
 * ----------------------------
 *   Auto-refresh is HARD-LOCKED at 60 seconds (60_000 ms) to honour the
 *   v6/v7 polling slowdown. DO NOT lower this. The setInterval below is
 *   the single source of truth for this page's cadence.
 */
const PROOF_OF_WORK_REFRESH_MS = 60_000; // 60s \u2014 DO NOT LOWER

// Source badge palette \u2014 stays within the brand gold/orange family for
// cohesion with the rest of the admin chrome (Active Subpacks, Subpack
// Settings, etc.). Each tier gets enough contrast to be distinguishable
// at a glance.
const SOURCE_META = {
  single: {
    label: 'Single Position',
    accent: '#FFC20E',
    icon: Activity,
    desc: 'Direct user out-of-pocket purchase',
  },
  subpack: {
    label: 'Subpack Position',
    accent: '#5EE6A1',
    icon: Package,
    desc: 'Automated drip from an active Sub Pack',
  },
  rebuy: {
    label: 'Legacy Snowball',
    accent: '#7BC2FF',
    icon: Repeat,
    desc: 'Legacy pre-v17.5 snowball spawn (disabled going forward)',
  },
  auto_rebuy: {
    label: 'Auto Rebuy Position',
    accent: '#C29CFF',
    icon: Zap,
    desc: 'Reservoir-funded 5-position auto-purchase',
  },
  unknown: {
    label: 'Unknown',
    accent: '#9BA3AE',
    icon: HelpCircle,
    desc: 'Pre-v10 legacy position (origin not recorded)',
  },
};

const fmtIso = (iso) => {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const d = new Date(t);
  // Compact dual-line layout: HH:MM:SS on top, YYYY-MM-DD beneath.
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const Y = d.getFullYear();
  const M = String(d.getMonth() + 1).padStart(2, '0');
  const D = String(d.getDate()).padStart(2, '0');
  return (
    <span className="inline-flex flex-col leading-[1.1] sc-tabular text-[11px]">
      <span className="text-[var(--sc-text)]">{`${hh}:${mm}:${ss}`}</span>
      <span className="text-[var(--sc-text-mute)] text-[10px]">{`${Y}-${M}-${D}`}</span>
    </span>
  );
};

// 8-character position ID display \u2014 enough to be visually distinct in a
// table while staying compact. The full ID is kept in the title attribute
// for copy / lookup.
const shortPid = (pid) => (pid ? String(pid).slice(0, 8) : '—');

const Badge = ({ source }) => {
  const meta = SOURCE_META[source] || SOURCE_META.unknown;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap"
      style={{
        background: `${meta.accent}1a`,
        color: meta.accent,
        border: `1px solid ${meta.accent}44`,
      }}
      title={meta.desc}
      data-testid={`pow-badge-${source}`}
    >
      <Icon className="w-3 h-3" strokeWidth={2.2} />
      {meta.label}
    </span>
  );
};

const SelectionTable = ({ title, phase, rows, accent }) => (
  <div className="sc-card" data-testid={`pow-table-${phase}`}>
    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{
            background: `${accent}1f`,
            border: `1px solid ${accent}55`,
            color: accent,
          }}
        >
          <ShieldCheck className="w-4 h-4" strokeWidth={2.2} />
        </div>
        <div className="font-display font-bold text-base text-[var(--sc-text)]">{title}</div>
        <span className="sc-pill ml-1" style={{ padding: '2px 8px', fontSize: 9 }}>
          <span className="sc-dot" />
          {rows.length} rows
        </span>
      </div>
    </div>

    {rows.length === 0 ? (
      <div className="flex flex-col items-center justify-center py-10 gap-2 text-center" data-testid={`pow-empty-${phase}`}>
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center"
          style={{
            background: `${accent}10`,
            border: `1px solid ${accent}33`,
          }}
        >
          <ShieldCheck className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div className="font-display text-sm text-[var(--sc-text-dim)]">No {phase} selections logged yet</div>
        <div className="text-[11px] text-[var(--sc-text-mute)] max-w-sm">
          The heartbeat will append a row here on the next successful cycle. If the engine is paused or filler-starved, the log will fill in slowly.
        </div>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" data-testid={`pow-${phase}-rows`}>
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] border-b border-[rgba(255,182,18,0.10)]">
              <th className="py-2 pr-3 font-medium">Position</th>
              <th className="py-2 pr-3 font-medium">Username</th>
              <th className="py-2 pr-3 font-medium">Type</th>
              <th className="py-2 pr-3 font-medium">Purchase / Creation</th>
              <th className="py-2 pr-3 font-medium text-right">Selected</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr
                key={`${r.position_id}-${r.selected_at}-${idx}`}
                className="border-b border-[rgba(255,182,18,0.06)] hover:bg-[rgba(255,182,18,0.03)] transition-colors"
                data-testid={`pow-${phase}-row-${idx}`}
              >
                <td className="py-2.5 pr-3 sc-tabular text-[var(--sc-text-dim)]" title={r.position_id} data-testid={`pow-${phase}-pid`}>
                  {shortPid(r.position_id)}
                </td>
                <td className="py-2.5 pr-3 text-[var(--sc-text)] font-medium">
                  {r.username || <span className="text-[var(--sc-text-mute)] italic">unknown</span>}
                </td>
                <td className="py-2.5 pr-3">
                  <Badge source={r.source || 'unknown'} />
                </td>
                <td className="py-2.5 pr-3" data-testid={`pow-${phase}-created`}>
                  {fmtIso(r.created_at)}
                </td>
                <td className="py-2.5 pr-3 text-right" data-testid={`pow-${phase}-selected`}>
                  {fmtIso(r.selected_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const ProofOfWorkSubpage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errored, setErrored] = useState(false);

  const reqIdRef = useRef(0);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async ({ silent } = {}) => {
    const reqId = ++reqIdRef.current;
    if (!silent) {
      if (!hasLoadedRef.current) setLoading(true);
      else setRefreshing(true);
    }
    try {
      const d = await fetchProofOfWorkSelections();
      if (reqId !== reqIdRef.current) return;
      setData(d);
      setErrored(false);
      hasLoadedRef.current = true;
    } catch {
      if (reqId !== reqIdRef.current) return;
      setErrored(true);
      if (!silent) toast.error('Failed to load Proof of Work selections');
    } finally {
      if (reqId === reqIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await load();
    })();
    const id = setInterval(() => {
      if (!alive) return;
      (async () => {
        if (!alive) return;
        await load({ silent: true });
      })();
    }, PROOF_OF_WORK_REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [load]);

  if (loading && !data) {
    return (
      <div className="space-y-6" data-testid="pow-loading">
        <div className="sc-card h-[68px] animate-pulse" />
        <div className="sc-card h-[360px] animate-pulse" />
        <div className="sc-card h-[360px] animate-pulse" />
      </div>
    );
  }

  if (errored && !data) {
    return (
      <div className="sc-card flex flex-col items-center justify-center py-12 gap-3" data-testid="pow-error">
        <AlertTriangle className="w-6 h-6 text-[#FF7A00]" />
        <div className="text-sm text-[var(--sc-text-dim)]">Could not load the Proof of Work log.</div>
        <button type="button" onClick={() => load()} className="sc-btn-gold" style={{ padding: '8px 16px', fontSize: 12 }} data-testid="pow-retry">
          Retry
        </button>
      </div>
    );
  }

  const fillers = data?.fillers || [];
  const roots = data?.roots || [];
  const logSize = Number(data?.log_size ?? 0);
  const logCap = Number(data?.log_cap ?? 10000);

  return (
    <div className="space-y-5" data-testid="proof-of-work-page">
      {/* Live engine HUD (owner request, 28 Sep 2026): its own 10 s refresh; the tables below keep 60 s. */}
      <ProofOfWorkLivePanel />

      {/* Sanity check banner ------------------------------------------- */}
      <div
        className="sc-card"
        style={{
          background: 'linear-gradient(135deg, rgba(255,194,14,0.08), rgba(255,122,0,0.05))',
          borderColor: 'rgba(255,194,14,0.32)',
        }}
        data-testid="pow-banner"
      >
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{
              background: 'rgba(255,194,14,0.15)',
              border: '1px solid rgba(255,194,14,0.40)',
              color: '#FFC20E',
            }}
          >
            <Info className="w-4 h-4" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-sm text-[#FFE08A] mb-1">Sanity Check</div>
            <div className="text-[12.5px] text-[var(--sc-text-dim)] leading-[1.55]">
              If the <span className="font-semibold text-[#FFE08A]">Purchase / Creation</span> timestamps below are in strict sequential/chronological order,
              the engine is running on FIFO. If the Purchase Timestamps are <span className="font-semibold text-[#5EE6A1]">mixed</span> (showing recent, old,
              and medium timestamps jumbled together), uniform random distribution is active and working correctly.
            </div>
            <div className="text-[11px] text-[var(--sc-text-mute)] mt-2 leading-[1.55]">
              Note: positions queued before this feature was deployed display an{' '}
              <span
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5"
                style={{
                  background: 'rgba(155,163,174,0.10)',
                  color: '#9BA3AE',
                  border: '1px solid rgba(155,163,174,0.30)',
                  fontSize: 10,
                }}
              >
                <HelpCircle className="w-2.5 h-2.5" /> Unknown
              </span>{' '}
              badge with a blank Purchase Timestamp. These cycle out within a few hours and the page becomes fully tagged.
            </div>
          </div>
        </div>
      </div>

      {/* Status strip --------------------------------------------------- */}
      <div className="sc-card flex items-center justify-between gap-3 flex-wrap py-3">
        <div className="flex items-center gap-3 text-[11px] text-[var(--sc-text-mute)] uppercase tracking-[0.18em]">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-[#FFC20E]" />
            Live engine log
          </span>
          <span className="text-[var(--sc-text-mute)]">·</span>
          <span className="sc-tabular text-[var(--sc-text-dim)]">
            {logSize.toLocaleString()} / {logCap.toLocaleString()} rows
          </span>
          <span className="sc-pill" style={{ padding: '2px 8px', fontSize: 9 }} title="Page auto-refreshes every 60 seconds">
            <span className="sc-dot" />
            auto · 60s
          </span>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[rgba(255,182,18,0.32)] text-[12px] text-[var(--sc-text-dim)] hover:border-[#FFC20E] hover:text-[#FFE08A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="pow-refresh-btn"
        >
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      {/* Tables --------------------------------------------------------- */}
      <SelectionTable title="Last 50 Selected Fillers (Phase 1)" phase="filler" rows={fillers} accent="#FFC20E" />
      <SelectionTable title="Last 50 Selected Roots (Phase 2)" phase="root" rows={roots} accent="#FFAD00" />
    </div>
  );
};

export default ProofOfWorkSubpage;
